/**
 * Mise en forme du français : heures, directions, durées, forces de vent.
 *
 * Aucune fonction ne lit l'horloge : l'instant courant est toujours passé en
 * paramètre. C'est ce qui rend ces sorties testables, et ce sont elles que
 * l'utilisateur lit en premier.
 */
import { normalizeDeg } from './geo';

/**
 * Heure réelle plutôt que "il y a X minutes" : plus lisible d'un coup d'œil,
 * et surtout vérifiable par l'utilisateur qui peut comparer à sa montre.
 */
export function formatClock(at: number | null, now: number): string {
  if (at === null || !Number.isFinite(at)) return '-';
  const date = new Date(at);
  const today = new Date(now);
  const time = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

  if (date.toDateString() === today.toDateString()) return time;

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return `hier ${time}`;

  return `${date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} ${time}`;
}

/** "aujourd'hui", "hier", ou une date en clair. */
export function dayLabel(dayOffset: number, now: number): string {
  if (dayOffset === 0) return "aujourd'hui";
  if (dayOffset === -1) return 'hier';
  const date = new Date(now);
  date.setDate(date.getDate() + dayOffset);
  return `le ${date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}`;
}

/** Date au format `AAAA-MM-JJ`, en heure locale, pour les requêtes satellite. */
export function isoDay(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

const SECTORS: readonly (readonly [number, string])[] = [
  [22.5, 'nord'],
  [67.5, 'nord-est'],
  [112.5, 'est'],
  [157.5, 'sud-est'],
  [202.5, 'sud'],
  [247.5, 'sud-ouest'],
  [292.5, 'ouest'],
  [337.5, 'nord-ouest'],
];

/** Le secteur cardinal correspondant à un cap. */
export function directionWord(deg: number): string {
  const value = normalizeDeg(deg);
  for (const [limit, word] of SECTORS) {
    if (value < limit) return word;
  }
  return 'nord';
}

/** "au nord", mais "à l'est" : l'est et l'ouest demandent l'élision. */
export function directionTo(deg: number): string {
  const word = directionWord(deg);
  return word === 'est' || word === 'ouest' ? `à l'${word}` : `au ${word}`;
}

/** "du nord", mais "de l'ouest". */
export function directionFrom(deg: number): string {
  const word = directionWord(deg);
  return word === 'est' || word === 'ouest' ? `de l'${word}` : `du ${word}`;
}

/** Force du vent en mots : un chiffre en km/h ne parle pas à tout le monde. */
export function forceWord(kmh: number | null): string {
  if (kmh === null || !Number.isFinite(kmh)) return "pas d'info";
  if (kmh < 5) return 'presque nul';
  if (kmh < 12) return 'faible';
  if (kmh < 25) return 'modéré';
  if (kmh < 40) return 'soutenu';
  return 'fort';
}

/**
 * Durée arrondie de façon lisible : "40 min", "1 h 30", "4 h".
 *
 * L'arrondi se fait sur le total en minutes, puis on répartit en heures et
 * quarts d'heure. La version précédente arrondissait les minutes séparément
 * et pouvait afficher "1 h 60".
 */
export function durationWords(hours: number | null): string {
  if (hours === null || !Number.isFinite(hours) || hours <= 0) return 'moins de 5 min';

  const totalMinutes = Math.round(hours * 60);
  if (totalMinutes < 60) {
    return `${Math.max(5, Math.round(totalMinutes / 5) * 5)} min`;
  }

  const quarters = Math.round(totalMinutes / 15);
  const wholeHours = Math.floor(quarters / 4);
  const minutes = (quarters % 4) * 15;

  // Au-delà de trois heures, le quart d'heure n'apporte plus rien.
  if (wholeHours >= 3) return `${Math.round(hours)} h`;
  return minutes === 0 ? `${wholeHours} h` : `${wholeHours} h ${minutes}`;
}

/** Nombre affiché : une décimale sous 10, entier au-dessus. */
export function formatConcentration(value: number): string {
  return value < 10 ? value.toFixed(1) : String(Math.round(value));
}

/** Âge d'un instant, en minutes. */
export function ageInMinutes(at: number, now: number): number {
  return (now - at) / 60_000;
}

/**
 * Arrondit un instant au pas donné, en minutes.
 *
 * Une heure d'arrivée de fumée calculée à partir d'un vent modélisé n'a pas
 * de sens à la minute. L'afficher au quart d'heure est déjà généreux, mais au
 * moins la forme ne promet plus une précision que le calcul n'a pas.
 */
export function roundTimeToStep(at: number, stepMinutes: number): number {
  const step = stepMinutes * 60_000;
  return Math.round(at / step) * step;
}
